import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import { apiPatch } from '../lib/api';
import { useAuth } from './AuthContext';
import { useMe as useMeQuery } from '../lib/queries';
import type { Hero } from '../types';

export type { Hero };

interface MeContextValue {
  hero: Hero;
  heroCoins: number;
  refreshHero: () => Promise<void>;
  updateCurrentRealm: (realmId: string) => Promise<boolean>;
  updateHeroName: (newName: string) => Promise<boolean>;
  updateCoins: (delta: number) => void;
  logout: () => void;
}

const MeContext = createContext<MeContextValue | null>(
  null
);

interface MeProviderProps {
  children: ReactNode;
}

export function MeProvider({ children }: MeProviderProps) {
  const { logout: authLogout } = useAuth();

  // Use React Query for hero data
  const {
    data: hero,
    isLoading,
    error,
    refetch: refreshHero,
  } = useMeQuery();

  const [heroCoins, setHeroCoins] = useState(0);

  // Update heroCoins when hero data changes
  useEffect(() => {
    if (hero) {
      setHeroCoins(hero.hero.coins);
    }
  }, [hero]);

  const logout = useCallback(() => {
    authLogout();
  }, [authLogout]);

  const updateCurrentRealm = useCallback(
    async (realmId: string): Promise<boolean> => {
      const result = await apiPatch<{
        defaultRealmId: string;
      }>('/api/me', {
        defaultRealmId: realmId,
      });

      if (result.ok) {
        // Trigger a refetch to get updated data
        refreshHero();
        return true;
      }
      return false;
    },
    [refreshHero]
  );

  const updateHeroName = useCallback(
    async (newName: string): Promise<boolean> => {
      const result = await apiPatch<{
        displayName: string;
      }>('/api/me', {
        displayName: newName,
      });

      if (result.ok) {
        // Trigger a refetch to get updated data
        refreshHero();
        return true;
      }
      return false;
    },
    [refreshHero]
  );

  const updateCoins = useCallback((delta: number) => {
    setHeroCoins(prev => prev + delta);
  }, []);

  const contextValue = useMemo(() => {
    if (!hero) return null;
    return {
      hero: hero.hero,
      heroCoins,
      refreshHero: async () => {
        await refreshHero();
      },
      updateCurrentRealm,
      updateHeroName,
      updateCoins,
      logout,
    };
  }, [
    hero,
    heroCoins,
    refreshHero,
    updateCurrentRealm,
    updateHeroName,
    updateCoins,
    logout,
  ]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deep-blue-900">
        <div className="font-pixel text-neon-blue-400">
          Loading...
        </div>
      </div>
    );
  }

  if (error || !contextValue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deep-blue-900">
        <div className="font-pixel text-red-400">
          {(error as Error)?.message ||
            'Failed to load hero'}
        </div>
      </div>
    );
  }

  return (
    <MeContext.Provider value={contextValue}>
      {children}
    </MeContext.Provider>
  );
}

export function useMe(): MeContextValue {
  const context = useContext(MeContext);
  if (!context) {
    throw new Error(
      'useMe must be used within a MeProvider'
    );
  }
  return context;
}
